'use client'

import { useEffect, useRef } from 'react'
import { logError } from '../../Helpers'
import { PlatformEventScopes } from '../constants/PlatformEventsScopes'
import { PlatformEventEvents } from '../constants/PlatformEventsEvents'
import { useBuildPlatformEventsUrl } from './useBuildPlatformEventsUrl'

export interface UsePlatformEventsPropsBase {
    /** @todo should eventually be required */
    scope?: PlatformEventScopes
    /** @todo should eventually be required */
    code?: string
    event?: PlatformEventEvents
}

export interface UsePlatformEventsProps extends UsePlatformEventsPropsBase {
    onEvent?: (event: MessageEvent) => void | Promise<void>
    onError?: (error: Event) => void
}

/**
 * Set up a PlatformEvents SSE connection and pass through each received event to callbacks.
 *
 * @example
 * ```ts
 * usePlatformEvents({
 *     scope: PlatformEventScopes.Company,
 *     code: companyCode,
 *     event: PlatformEventEvents.AddressUpdated,
 *     onEvent: (messageEvent) => {
 *         console.log(messageEvent.data)
 *     },
 * })
 * ```
 *
 * @param { scope } - Scope in which to listen for events.
 * @param { code } - Identifier inside the scope (for example a company code).
 * @param { event } - Optional custom event name; defaults to the SSE "message" event.
 * @param { onEvent } - Callback that is invoked for each incoming event.
 * @param { onError } - Optional callback that is invoked when the EventSource errors.
 */
export const usePlatformEvents = ({ scope, code, event, onEvent, onError }: UsePlatformEventsProps): void => {
    const url = useBuildPlatformEventsUrl(scope, code)
    /** Refs avoid accidental eventSource closures */
    const onEventRef = useRef(onEvent)
    const onErrorRef = useRef(onError)

    useEffect(() => {
        onEventRef.current = onEvent
    }, [onEvent])

    useEffect(() => {
        onErrorRef.current = onError
    }, [onError])

    useEffect(() => {
        if (!url) {
            return
        }

        const eventSource = new EventSource(url, { withCredentials: true })

        const controller = new AbortController()
        const signal = controller.signal

        const DEFAULT_EVENT = 'message'

        const onMessageEvent = (messageEvent: MessageEvent) => onEventRef.current?.(messageEvent)

        eventSource.addEventListener(event ?? DEFAULT_EVENT, onMessageEvent, { signal })

        eventSource.onerror = (error) => {
            onErrorRef.current?.(error)
            logError(`PlatformEvent connection error: ${error.toString()}`, { caller: 'usePlatformEvent onError' })
        }

        return () => {
            controller.abort()
            eventSource.close()
        }
    }, [url, event])
}
